/**
 * User Service
 * Handles user profile and user-related API calls
 */

import axiosInstance, { apiRequest } from '../axiosInstance';
import { USER_ENDPOINTS, buildQueryString } from '../endpoints';

/**
 * @typedef {Object} UserProfile
 * @property {number} id - User ID
 * @property {string} email - Email address
 * @property {string} firstName - First name
 * @property {string} lastName - Last name
 * @property {string} fullName - Full name
 * @property {string} [profilePicture] - Profile picture URL
 * @property {string} [role] - User role
 * @property {string} [company] - Company name
 * @property {string} [phone] - Phone number
 * @property {string} [location] - Location
 * @property {string} [bio] - User bio
 */

/**
 * @typedef {Object} UpdateProfileDto
 * @property {string} [firstName] - First name
 * @property {string} [lastName] - Last name
 * @property {string} [profilePicture] - Profile picture URL
 * @property {string} [phone] - Phone number
 * @property {string} [location] - Location
 * @property {string} [bio] - User bio
 * @property {string} [company] - Company name
 */

/**
 * @typedef {Object} UserStatistics
 * @property {number} formsCreated - Total forms created
 * @property {number} totalSubmissions - Total submissions
 * @property {number} activeForms - Active forms count
 * @property {number} daysActive - Days since registration
 */

/**
 * @typedef {Object} CreateUserWithRoleDto
 * @property {string} email - Email (max 320 chars, required)
 * @property {string} firstName - First name (max 200 chars, required)
 * @property {string} lastName - Last name (max 200 chars, required)
 * @property {string} password - Password (6-100 chars, required)
 * @property {number} organizationId - Organization ID (required)
 * @property {number} userRole - User role (0=SuperAdmin, 1=Admin, 2=User, required)
 * @property {string} [phone] - Phone number (max 50 chars)
 * @property {string} [profilePicture] - Profile picture URL (max 500 chars)
 */

/**
 * @typedef {Object} UpdateUserRoleDto
 * @property {number} userRole - User role (0=SuperAdmin, 1=Admin, 2=User, required)
 */

/**
 * @typedef {Object} UserListParams
 * @property {number} [organizationId] - Filter by organization ID
 * @property {boolean} [includeInactive] - Include inactive users (default: false)
 */

const userService = {
  /**
   * Get current user profile
   * @returns {Promise<UserProfile>} User profile data
   */
  getUserProfile: async () => {
    return apiRequest(() =>
      axiosInstance.get(USER_ENDPOINTS.PROFILE)
    );
  },

  /**
   * Update user profile
   * @param {UpdateProfileDto} profileData - Updated profile data
   * @returns {Promise<UserProfile>} Updated user profile
   */
  updateUserProfile: async (profileData) => {
    return apiRequest(() =>
      axiosInstance.put(USER_ENDPOINTS.UPDATE_PROFILE, profileData)
    );
  },

  /**
   * Get user statistics
   * @returns {Promise<UserStatistics>} User statistics
   */
  getUserStatistics: async () => {
    return apiRequest(() =>
      axiosInstance.get(USER_ENDPOINTS.STATISTICS)
    );
  },

  /**
   * Change the current user's password
   * @param {{ currentPassword: string, newPassword: string }} payload
   * @returns {Promise<void>}
   */
  changePassword: async ({ currentPassword, newPassword }) => {
    return apiRequest(() =>
      axiosInstance.post(USER_ENDPOINTS.CHANGE_PASSWORD, { currentPassword, newPassword })
    );
  },

  // ========== USER MANAGEMENT METHODS (Admin & Super Admin) ==========

  /**
   * Get list of users (Admin & Super Admin)
   * @param {UserListParams} [params] - Query parameters
   * @returns {Promise<Array>} List of users
   */
  getUsers: async (params = {}) => {
    const queryString = buildQueryString(params);
    return apiRequest(() =>
      axiosInstance.get(`${USER_ENDPOINTS.LIST}${queryString}`)
    );
  },

  /**
   * Get user by ID (Admin & Super Admin)
   * @param {number} id - User ID
   * @returns {Promise<Object>} User data
   */
  getUserById: async (id) => {
    return apiRequest(() =>
      axiosInstance.get(USER_ENDPOINTS.GET_BY_ID(id))
    );
  },

  /**
   * Create new user with role (Admin & Super Admin)
   * @param {CreateUserWithRoleDto} userData - User data
   * @returns {Promise<Object>} Created user
   */
  createUser: async (userData) => {
    return apiRequest(() =>
      axiosInstance.post(USER_ENDPOINTS.CREATE, userData)
    );
  },

  /**
   * Update user (Admin & Super Admin)
   * @param {number} id - User ID
   * @param {UpdateProfileDto} userData - Updated user data
   * @returns {Promise<Object>} Updated user
   */
  updateUser: async (id, userData) => {
    return apiRequest(() =>
      axiosInstance.put(USER_ENDPOINTS.UPDATE(id), userData)
    );
  },

  /**
   * Delete user (Admin & Super Admin)
   * @param {number} id - User ID
   * @returns {Promise<void>}
   */
  deleteUser: async (id) => {
    return apiRequest(() =>
      axiosInstance.delete(USER_ENDPOINTS.DELETE(id))
    );
  },

  /**
   * Update user role (Admin & Super Admin)
   * @param {number} id - User ID
   * @param {UpdateUserRoleDto} roleData - Role update data
   * @returns {Promise<Object>} Updated user
   */
  updateUserRole: async (id, roleData) => {
    return apiRequest(() =>
      axiosInstance.put(USER_ENDPOINTS.UPDATE_ROLE(id), roleData)
    );
  },

  /**
   * Activate user (Admin & Super Admin)
   * @param {number} id - User ID
   * @returns {Promise<Object>} Updated user
   */
  activateUser: async (id) => {
    return apiRequest(() =>
      axiosInstance.post(USER_ENDPOINTS.ACTIVATE(id))
    );
  },

  /**
   * Deactivate user (Admin & Super Admin)
   * @param {number} id - User ID
   * @returns {Promise<Object>} Updated user
   */
  deactivateUser: async (id) => {
    return apiRequest(() =>
      axiosInstance.post(USER_ENDPOINTS.DEACTIVATE(id))
    );
  },

  // ========== AVATAR METHODS ==========

  /**
   * Upload avatar image for current user
   * @param {File} file - Image file to upload
   * @returns {Promise<UserProfile>} Updated user profile
   */
  uploadAvatar: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiRequest(() =>
      axiosInstance.post(USER_ENDPOINTS.UPLOAD_AVATAR, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
    );
  },

  /**
   * Remove avatar for current user
   * @returns {Promise<UserProfile>} Updated user profile
   */
  removeAvatar: async () => {
    return apiRequest(() =>
      axiosInstance.delete(USER_ENDPOINTS.REMOVE_AVATAR)
    );
  },

  // ========== HELPER METHODS ==========

  /**
   * Helper: Get user initials from name
   * @param {string} name - User's full name
   * @returns {string} User initials (max 2 chars)
   */
  getUserInitials: (name) => {
    if (!name) return 'U';
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  },

  /**
   * Helper: Format user display name
   * @param {UserProfile} user - User profile
   * @returns {string} Formatted display name
   */
  getDisplayName: (user) => {
    if (user.fullName) return user.fullName;
    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    if (user.firstName) return user.firstName;
    return user.email || 'Unknown User';
  },

  /**
   * Helper: Validate profile update data
   * @param {UpdateProfileDto} profileData - Profile data to validate
   * @returns {Object} Validation result { isValid: boolean, errors: string[] }
   */
  validateProfileData: (profileData) => {
    const errors = [];

    if (profileData.firstName && profileData.firstName.length > 200) {
      errors.push('First name must not exceed 200 characters');
    }

    if (profileData.lastName && profileData.lastName.length > 200) {
      errors.push('Last name must not exceed 200 characters');
    }

    if (profileData.profilePicture && profileData.profilePicture.length > 500) {
      errors.push('Profile picture URL must not exceed 500 characters');
    }

    if (profileData.phone && profileData.phone.length > 50) {
      errors.push('Phone number must not exceed 50 characters');
    }

    if (profileData.location && profileData.location.length > 200) {
      errors.push('Location must not exceed 200 characters');
    }

    if (profileData.bio && profileData.bio.length > 1000) {
      errors.push('Bio must not exceed 1000 characters');
    }

    if (profileData.company && profileData.company.length > 200) {
      errors.push('Company name must not exceed 200 characters');
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  },

  /**
   * Helper: Validate create user data
   * @param {CreateUserWithRoleDto} userData - User data to validate
   * @returns {Object} Validation result { isValid: boolean, errors: string[] }
   */
  validateCreateUserData: (userData) => {
    const errors = [];

    // Required fields
    if (!userData.email || userData.email.trim() === '') {
      errors.push('Email is required');
    }

    if (!userData.firstName || userData.firstName.trim() === '') {
      errors.push('First name is required');
    }

    if (!userData.lastName || userData.lastName.trim() === '') {
      errors.push('Last name is required');
    }

    if (!userData.password || userData.password.trim() === '') {
      errors.push('Password is required');
    }

    if (userData.organizationId === undefined || userData.organizationId === null) {
      errors.push('Organization is required');
    }

    if (userData.userRole === undefined || userData.userRole === null) {
      errors.push('User role is required');
    }

    // Length validations
    if (userData.email && userData.email.length > 320) {
      errors.push('Email must not exceed 320 characters');
    }

    if (userData.firstName && userData.firstName.length > 200) {
      errors.push('First name must not exceed 200 characters');
    }

    if (userData.lastName && userData.lastName.length > 200) {
      errors.push('Last name must not exceed 200 characters');
    }

    if (userData.password) {
      if (userData.password.length < 6) {
        errors.push('Password must be at least 6 characters');
      }
      if (userData.password.length > 100) {
        errors.push('Password must not exceed 100 characters');
      }
    }

    if (userData.phone && userData.phone.length > 50) {
      errors.push('Phone must not exceed 50 characters');
    }

    if (userData.profilePicture && userData.profilePicture.length > 500) {
      errors.push('Profile picture URL must not exceed 500 characters');
    }

    // Email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (userData.email && !emailRegex.test(userData.email)) {
      errors.push('Email must be a valid email address');
    }

    // Role validation
    if (userData.userRole !== undefined && ![0, 1, 2].includes(userData.userRole)) {
      errors.push('Invalid user role');
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  },

  /**
   * TEMPORARY: Mock update profile (client-side only)
   * This updates localStorage until backend endpoint is available
   * @param {UpdateProfileDto} profileData - Updated profile data
   * @returns {Promise<UserProfile>} Updated user profile
   */
  mockUpdateProfile: async (profileData) => {
    return new Promise((resolve) => {
      setTimeout(() => {
        const userData = localStorage.getItem('userData');
        if (userData) {
          const user = JSON.parse(userData);
          const updatedUser = { ...user, ...profileData };

          // Update full name if first/last name changed
          if (profileData.firstName || profileData.lastName) {
            updatedUser.fullName = `${profileData.firstName || user.firstName} ${profileData.lastName || user.lastName}`;
          }

          localStorage.setItem('userData', JSON.stringify(updatedUser));
          if (updatedUser.fullName) {
            localStorage.setItem('fullName', updatedUser.fullName);
          }
          if (updatedUser.role) {
            localStorage.setItem('role', updatedUser.role);
          }

          resolve(updatedUser);
        }
      }, 500); // Simulate network delay
    });
  },

  /**
   * TEMPORARY: Mock user statistics (client-side only)
   * Returns mock data until backend endpoint is available
   * @returns {Promise<UserStatistics>} Mock statistics
   */
  mockGetStatistics: async () => {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          formsCreated: 12,
          totalSubmissions: 247,
          activeForms: 8,
          daysActive: 45,
        });
      }, 300);
    });
  },
};

export default userService;
